import * as React from 'react';
import { cn } from '@/lib/utils';
import { TrendingDown, TrendingUp, type LucideIcon } from 'lucide-react';

type Tone = 'default' | 'primary' | 'accent' | 'success' | 'warning' | 'destructive' | 'info';

/**
 * Operational Quiet: KPI tiles are pure white. The icon chip is neutral by
 * default; it picks up tone color ONLY for status-loaded KPIs (success /
 * warning / destructive / info / accent). 'primary' and 'default' both render
 * neutral so saffron stays scarce and meaningful.
 */
const ICON_CHIP: Record<Tone, string> = {
  default: 'bg-muted text-muted-foreground',
  primary: 'bg-muted text-foreground',
  accent: 'bg-accent-50 text-accent-700',
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/15 text-[hsl(38_85%_30%)]',
  destructive: 'bg-destructive/10 text-destructive',
  info: 'bg-info/10 text-info',
};

interface Props {
  label: string;
  value: React.ReactNode;
  unit?: string;
  hint?: React.ReactNode;
  tone?: Tone;
  icon?: LucideIcon;
  delta?: { value: number; direction: 'up' | 'down'; label?: string };
  className?: string;
}

export function KpiCard({
  label,
  value,
  unit,
  hint,
  tone = 'default',
  icon: Icon,
  delta,
  className,
}: Props) {
  return (
    <div
      className={cn(
        'group relative rounded-lg border border-border bg-card p-4 transition-colors',
        'hover:border-border/100 hover:bg-muted/30',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        {Icon && (
          <span className={cn('grid h-8 w-8 place-items-center rounded-md', ICON_CHIP[tone])}>
            <Icon className="h-[15px] w-[15px]" strokeWidth={2} />
          </span>
        )}
        {delta && (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[11px] font-semibold',
              delta.direction === 'up'
                ? 'text-success'
                : 'text-destructive',
            )}
          >
            {delta.direction === 'up' ? (
              <TrendingUp className="h-3 w-3" />
            ) : (
              <TrendingDown className="h-3 w-3" />
            )}
            {Math.abs(delta.value).toFixed(1)}%
          </span>
        )}
      </div>

      <div className="mt-3 text-[12px] font-medium text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <div className="text-[28px] font-semibold leading-none tracking-[-0.02em] text-foreground tabular-nums">
          {value}
        </div>
        {unit && <div className="text-xs font-medium text-muted-foreground">{unit}</div>}
      </div>
      {hint && (
        <div className="mt-1.5 text-[11.5px] text-muted-foreground">{hint}</div>
      )}
    </div>
  );
}
